import React, { useEffect, useRef, useState } from "react";
import { getTokens } from "./auth/keycloakClient";
import logo2 from "./logo2.svg";
import TestcaseParser from "./testcaseParser";
import RobotFrameworkParser from "./robotFrameworkParser";
import ExpandButton from "./components/ExpandButton";
import CopyButton from "./components/CopyButton";
import { InfoIcon, ChipIcon } from "./components/Icons";
import CompartmentList from "./components/CompartmentList";
import { FaRegFileCode } from "react-icons/fa";



import { EXECUTE_URL, TESTCASE_QUERY, REVIEWS_QUERY, TEXTAREA_CONFIG } from "./config/constants";


export default function App() {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Separate states for structured output
  const [analysis, setAnalysis] = useState("");
  const [testCasesText, setTestCasesText] = useState("");
  const [robotFrameworkCode, setRobotFrameworkCode] = useState("");
  const [savedRobotFrameworkCode, setSavedRobotFrameworkCode] = useState("");
  const [showRobotFramework, setShowRobotFramework] = useState(false);
  const [isPrintingRobot, setIsPrintingRobot] = useState(false);
  const [isGeneratingRobot, setIsGeneratingRobot] = useState(false);

  // Dynamic testcases and expansion states
  const [dynamicTestcases, setDynamicTestcases] = useState([]);
  const [expandedTestcases, setExpandedTestcases] = useState({});

  // Dynamic Robot Framework tests and expansion states
  const [dynamicRobotTests, setDynamicRobotTests] = useState([]);
  const [expandedRobotTests, setExpandedRobotTests] = useState({});

  // Click expansion state
  const [expandedPanel, setExpandedPanel] = useState(null);

  // Streaming toggle
  const [isStreamingEnabled, setIsStreamingEnabled] = useState(true);

  const currentSectionRef = useRef("analysis");
  const textBufferRef = useRef("");

  const textareaRef = useRef(null);
  const testcaseParser = useRef(null);
  const robotFrameworkParser = useRef(null);
  const abortRef = useRef(null);
  const scrollRefAnalysis = useRef(null);
  const scrollRefTestCases = useRef(null);
  const scrollRefCode = useRef(null);

  // Initialize Parsers
  useEffect(() => {
    testcaseParser.current = new TestcaseParser();
    robotFrameworkParser.current = new RobotFrameworkParser();
  }, []);

  // Auto-resize textarea function
  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, TEXTAREA_CONFIG.MAX_HEIGHT);
      textarea.style.height = newHeight + 'px';

      // Show scrollbar only when content exceeds max height
      textarea.style.overflowY = textarea.scrollHeight > TEXTAREA_CONFIG.MAX_HEIGHT ? 'auto' : 'hidden';
    }
  };

  // Handle question change with auto-resize
  const handleQuestionChange = (e) => {
    setQuestion(e.target.value);
    setTimeout(autoResizeTextarea, 0); // Run after state update
  };

  // Handle panel click to expand/collapse
  const handlePanelClick = (panelId) => {
    setExpandedPanel(expandedPanel === panelId ? null : panelId);
  };

  // Keep each panel scrolled to bottom while streaming
  useEffect(() => {
    if (scrollRefAnalysis.current) {
      scrollRefAnalysis.current.scrollTop = scrollRefAnalysis.current.scrollHeight;
    }
  }, [analysis, loading]);

  useEffect(() => {
    if (scrollRefTestCases.current) {
      scrollRefTestCases.current.scrollTop = scrollRefTestCases.current.scrollHeight;
    }
  }, [testCasesText, loading]);

  useEffect(() => {
    if (scrollRefCode.current) {
      scrollRefCode.current.scrollTop = scrollRefCode.current.scrollHeight;
    }
  }, [robotFrameworkCode, loading]);

  const handleStreamOutput = (outputText, isNewMsg = false) => {
    if (isNewMsg) {
      setAnalysis("");
      setTestCasesText("");
      setRobotFrameworkCode("");
      setDynamicTestcases([]);
      setDynamicRobotTests([]);
      setIsGeneratingRobot(false);
      currentSectionRef.current = "analysis";
      textBufferRef.current = "";

      // Reset parsers
      if (testcaseParser.current) {
        testcaseParser.current.resetState();
      }
      if (robotFrameworkParser.current) {
        robotFrameworkParser.current.resetState();
      }
      return;
    }

    if (!outputText) return;

    // Add new text to buffer
    textBufferRef.current += outputText;
    let bufferContent = textBufferRef.current;

    // Define markers as single words
    const ANALYSIS_MARKER = "ENDOFANALYSIS";
    const TESTCASES_MARKER = "ENDOFTESTCASES";
    
    // Check for ENDOFANALYSIS marker
    if (currentSectionRef.current === "analysis") {
      const analysisIndex = bufferContent.indexOf(ANALYSIS_MARKER);

      if (analysisIndex !== -1) {
        
        // Get text before and after marker
        const beforeMarker = bufferContent.substring(0, analysisIndex).trim();
        const afterMarkerStart = analysisIndex + ANALYSIS_MARKER.length;
        const afterMarker = bufferContent.substring(afterMarkerStart).trim();
        
        // Add everything before marker to analysis
        if (beforeMarker) {
          setAnalysis((prev) => prev + beforeMarker);
        }

        // Switch to testcases section
        currentSectionRef.current = "testcases";
        
        // Set the buffer to the text after marker
        textBufferRef.current = afterMarker;
        
        // If there's text after marker, process it immediately
        if (afterMarker && afterMarker.length > 0) {
          setTimeout(() => {
            if (currentSectionRef.current === "testcases" && textBufferRef.current) {
              // Check if the afterMarker contains ENDOFTESTCASES already
              const testcasesIndex = textBufferRef.current.indexOf(TESTCASES_MARKER);
              if (testcasesIndex === -1) {
                // No second marker yet, add all to testcases
                setTestCasesText((prev) => prev + textBufferRef.current);
                textBufferRef.current = "";
              } else {
                // Second marker found immediately, process it
                handleStreamOutput("", false);
              }
            }
          }, 10);
        }
        
        return;
      }
    }
    
    // Check for ENDOFTESTCASES marker
    if (currentSectionRef.current === "testcases") {
      const testcasesIndex = bufferContent.indexOf(TESTCASES_MARKER);

      if (testcasesIndex !== -1) {
        
        // Get text before and after marker
        const beforeMarker = bufferContent.substring(0, testcasesIndex).trim();
        const afterMarkerStart = testcasesIndex + TESTCASES_MARKER.length;
        const afterMarker = bufferContent.substring(afterMarkerStart).trim();
        
        // Add everything before marker to testcases
        if (beforeMarker) {
          setTestCasesText((prev) => prev + beforeMarker);

          // Process through parser for compartments
          if (testcaseParser.current) {
            const response = testcaseParser.current.processStreamContent({
              content: beforeMarker,
              section: "testcases"
            });

            if (response.isSuccessful && Array.isArray(response.content)) {
              setDynamicTestcases(response.content.filter(testcase =>
                testcase && testcase.id && typeof testcase.id === 'string'
              ));
            }
          }
        }
        // Switch to robot section
        currentSectionRef.current = "robot";
        setIsGeneratingRobot(true);

        // Set buffer to text after marker
        textBufferRef.current = afterMarker;

        // If there's text after marker, save it but don't display
        if (afterMarker && afterMarker.length > 0) {
          setTimeout(() => {
            if (currentSectionRef.current === "robot" && textBufferRef.current) {
              setSavedRobotFrameworkCode((prev) => prev + textBufferRef.current);
              textBufferRef.current = "";
            }
          }, 10);
        }
        
        return;
      }
    }
    
    // Check if we might be building a marker (partial detection)
    // With single-word markers, we need simpler partial detection
    const partialMarkers = [
      "ENDOFANALYSI", "ENDOFANALYS", "ENDOFANALY", "ENDOFANAL",
      "ENDOFANA", "ENDOFAN", "ENDOFA", "ENDOF", "ENDO", "END",
      "ENDOFTESTCASE", "ENDOFTESTCAS", "ENDOFTESTCA", "ENDOFTESTC",
      "ENDOFTEST", "ENDOFTES", "ENDOFTE", "ENDOFT"
    ];
    
    let mightBeMarker = false;
    for (const partial of partialMarkers) {
      if (bufferContent.endsWith(partial)) {
        mightBeMarker = true;
        break;
      }
    }
    
    if (!mightBeMarker) {
      // No marker or partial marker - flush buffer to current section
      if (currentSectionRef.current === "analysis") {
        if (isStreamingEnabled) {
          setAnalysis((prev) => prev + bufferContent);
        }
      } else if (currentSectionRef.current === "testcases") {
        if (isStreamingEnabled) {
          setTestCasesText((prev) => prev + bufferContent);

          // Process through parser for compartments
          if (testcaseParser.current) {
            const response = testcaseParser.current.processStreamContent({
              content: bufferContent,
              section: "testcases"
            });

            if (response.isSuccessful && Array.isArray(response.content)) {
              setDynamicTestcases(response.content.filter(testcase =>
                testcase && testcase.id && typeof testcase.id === 'string'
              ));
            }
          }
        }
      } else if (currentSectionRef.current === "robot") {
        // Robot framework ALWAYS just saves content, NEVER displays live
        // No matter if chat or button - always collect, never stream
        setSavedRobotFrameworkCode((prev) => prev + bufferContent);
      }
      // Only clear buffer when streaming is enabled - when OFF, keep accumulating for marker detection
      // Exception: always clear robot buffer since it's already saved
      if (isStreamingEnabled || currentSectionRef.current === "robot") {
        textBufferRef.current = "";
      }
    }
    // If mightBeMarker is true, keep text in buffer and wait for more
  };

  // Generic Stream-Handler function
  const handleStream = async (query) => {
    if (loading) return;

    // Immediate content refresh - clear everything to initial state
    setAnalysis("");
    setTestCasesText("");
    setRobotFrameworkCode("");
    setSavedRobotFrameworkCode("");
    setDynamicTestcases([]);
    setExpandedTestcases({});
    setDynamicRobotTests([]);
    setExpandedRobotTests({});
    setShowRobotFramework(false);  // Always reset showRobotFramework
    setIsGeneratingRobot(false);
    setErr(null);
    setLoading(true);
    currentSectionRef.current = "analysis";
    textBufferRef.current = "";

    // Reset parser
    if (testcaseParser.current) {
      testcaseParser.current.resetState();
    }

    try {
      const { access_token, refresh_token } = await getTokens();
      const body = {
        query: query,
        webhook_url: null,
        refresh_token: refresh_token || "",
        context_building_mode: "auto",
        execute_with_code_generation: false,
      };

      const controller = new AbortController();
      abortRef.current = controller;

      const resp = await fetch(EXECUTE_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${access_token}`,
          "Content-Type": "application/json",
          Accept: "text/event-stream, application/x-ndjson, application/json",
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!resp.ok || !resp.body) {
        const text = await resp.text().catch(() => "");
        throw new Error(`HTTP ${resp.status} ${resp.statusText}${text ? " - " + text : ""}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      const handleEventObject = (obj) => {
        const diana = obj?.diana_agent_execution;
        if (diana) {
          const output = diana.output ?? "";
          handleStreamOutput(output, !!diana.is_beginning_of_new_message);
        }

        if (Array.isArray(obj?.execution_layers)) {
          for (const layer of obj.execution_layers) {
            const execs = layer?.agent_executions || [];
            for (const ex of execs) {
              if (typeof ex?.output === "string" && ex.output.length > 0) {
                handleStreamOutput(ex.output, false);
              }
            }
          }
        }
      };

      const processNdjsonLine = (line) => {
        const s = line.trim();
        if (!s || s === "[DONE]") return;
        try {
          handleEventObject(JSON.parse(s));
        } catch {
          /* ignore */
        }
      };

      const processSseBlock = (block) => {
        const lines = block.split(/\r?\n/);
        const dataLines = [];
        for (const l of lines) if (l.startsWith("data:")) dataLines.push(l.slice(5).trim());
        if (!dataLines.length) return false;
        const payload = dataLines.join("\n").trim();
        if (!payload || payload === "[DONE]") return true;
        try {
          handleEventObject(JSON.parse(payload));
          return true;
        } catch {
          return false;
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let sseParts = buffer.split(/\r?\n\r?\n/);
        buffer = sseParts.pop() ?? "";
        for (const blk of sseParts) {
          const handled = processSseBlock(blk);
          if (!handled) blk.split(/\r?\n/).forEach(processNdjsonLine);
        }

        const ndjsonParts = buffer.split(/\r?\n/);
        buffer = ndjsonParts.pop() ?? "";
        ndjsonParts.forEach(processNdjsonLine);
      }

      if (buffer.trim()) {
        if (!processSseBlock(buffer)) processNdjsonLine(buffer);
      }
      
      // Final cleanup - flush any remaining buffer content
      if (textBufferRef.current) {
        const remaining = textBufferRef.current;
        if (currentSectionRef.current === "analysis") {
          setAnalysis((prev) => prev + remaining);
        } else if (currentSectionRef.current === "testcases") {
          setTestCasesText((prev) => prev + remaining);
        } else if (currentSectionRef.current === "robot") {
          // Just save the content, NEVER auto-display compartments from chat
          setSavedRobotFrameworkCode((prev) => prev + remaining);
        }
        textBufferRef.current = "";
      }
      
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error(e);
        setErr(e.message || "Streaming failed. Check Keycloak token / execute URL.");
      }
    } finally {
      setLoading(false);
      setIsGeneratingRobot(false);
      abortRef.current = null;
    }
  };

  const ask = async () => {
    const q = question.trim();
    if (!q || loading) return;
    await handleStream(q);
  };

  const generateTestcases = () => {
    handleStream(TESTCASE_QUERY);
  };

  const generateReviews = () => {
    handleStream(REVIEWS_QUERY);
  };

  const generateRobotFrameworkTests = () => {
    if (savedRobotFrameworkCode && !isPrintingRobot) {
      setIsPrintingRobot(true);
      setRobotFrameworkCode("");
      setDynamicRobotTests([]); // Clear compartments first
      setShowRobotFramework(true);

      // Process compartments and display content - same for both modes
      if (robotFrameworkParser.current) {
        robotFrameworkParser.current.resetState();

        const response = robotFrameworkParser.current.processStreamContent({
          content: savedRobotFrameworkCode,
          section: "robotframework"
        });

        if (response.isSuccessful && Array.isArray(response.content)) {
          setDynamicRobotTests(response.content.filter(robotTest =>
            robotTest && robotTest.id && typeof robotTest.id === 'string'
          ));
        }
      }

      // Always display content instantly for Robot Framework
      setRobotFrameworkCode(savedRobotFrameworkCode);
      setIsPrintingRobot(false);
    }
  };

  const cancel = () => abortRef.current?.abort();

  const clearAll = () => {
    setQuestion("");
    setAnalysis("");
    setTestCasesText("");
    setRobotFrameworkCode("");
    setSavedRobotFrameworkCode("");
    setShowRobotFramework(false);
    setIsPrintingRobot(false);
    setIsGeneratingRobot(false);
    setDynamicTestcases([]);
    setExpandedTestcases({});
    setDynamicRobotTests([]);
    setExpandedRobotTests({});
    setErr(null);
    currentSectionRef.current = "analysis";
    textBufferRef.current = "";

    // Reset parser
    if (testcaseParser.current) {
      testcaseParser.current.resetState();
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 text-gray-900">
      <div className="mx-auto px-4 py-4 h-screen">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-2rem)]">
          {/* LEFT: typing area (no inner scroll) */}
          <div className="bg-white rounded-2xl shadow-md flex flex-col">
            <div className="px-5 py-3 border-b">
              <h1 className="text-xl font-semibold"><span className="font-mono bg-purple-100 text-green-700 px-2 py-1 rounded">Testing-AI</span> Query Intake</h1>
            </div>
            <div className="p-5 flex-1 flex flex-col">

            <div className="space-y-3">
              {/* Testing-AI compartment */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-visible">
                <div className="bg-gradient-to-r from-purple-100 to-purple-50 px-4 py-2 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <ChipIcon className="w-5 h-5 text-purple-600" />
                    <span style={{marginTop: '-3px'}}>Test case and Robot Framework queries</span>
                  </h3>
                </div>
                <div className="p-4">
                <div className="flex flex-col space-y-4">
                  <div className="relative group">
                    <button
                      onClick={generateTestcases}
                      disabled={loading}
                      className="flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-700 transition text-center w-full"
                    >
                      Smart Home: Gap-Analysis & Test cases
                    </button>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[60]">
                      <div className="text-center">
                        <div>Analyzes current testing coverage for the function "Smart Home"</div>
                        <div>and identifies testing gaps in the testing of that function.</div>
                        <div>Generates detailed test case to close the testing gaps.</div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                  <div className="relative group">
                    <button
                      onClick={generateReviews}
                      disabled={loading}
                      className="flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-700 transition text-center w-full"
                    >
                      Amazon Reviews: Analysis & Test cases
                    </button>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[60]">
                      <div className="text-center">
                        <div>Analyses existing Amazon reviews for</div>
                        <div>different washing machines and compares</div>
                        <div>the complains with current testing coverage</div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                  <div className="border-t border-gray-300 my-4 -mx-4"></div>
                  <div className="relative group">
                    <button
                      onClick={generateRobotFrameworkTests}
                      disabled={loading || isPrintingRobot || !savedRobotFrameworkCode}
                      className="flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-700 transition text-center w-full"
                    >
                      {isPrintingRobot && (
                        <svg
                          className="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                      )}
                      Robot Framework Code
                    </button>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[60]">
                      <div className="text-center">
                        <div>Displays generated Robot Framework Code.</div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
                </div>
              </div>

              <div className="border-t border-gray-200 -mx-5"></div>

              {/* Chat input */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-visible">
                <div className="bg-gradient-to-r from-purple-100 to-purple-50 px-4 py-2 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                      <text x="12" y="16" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="bold" textAnchor="middle" fill="currentColor">?</text>
                    </svg>
                    <span style={{marginTop: '-3px'}}>Your question</span>
                  </h3>
                </div>
                <div className="p-0">
                <textarea
                  ref={textareaRef}
                  id="question"
                  value={question}
                  onChange={handleQuestionChange}
                  placeholder="Type your question here..."
                  rows="2"
                  className="w-full resize-y rounded-b-xl border-0 p-3 focus:outline-none focus:ring-0 block"
                  style={{
                    minHeight: TEXTAREA_CONFIG.MIN_HEIGHT,
                    maxHeight: `${TEXTAREA_CONFIG.MAX_HEIGHT}px`,
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgb(156 163 175) transparent',
                    display: 'block',
                    marginBottom: '0'
                  }}
                />
                </div>
              </div>

              <div className="border-t border-gray-200 -mx-5"></div>

              {/* Send, Clear and Cancel buttons row */}
              <div className="flex gap-2 justify-end">
                <button
                  onClick={ask}
                  disabled={loading || question.trim().length === 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-700 transition"
                >
                  {loading && (
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  )}
                  Send
                </button>
                <button
                  onClick={clearAll}
                  disabled={question.trim().length === 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700 transition"
                >
                  Clear
                </button>
                {loading && (
                  <button
                    onClick={cancel}
                    className="inline-flex items-center gap-2 rounded-lg bg-gray-600 px-4 py-2 text-white font-medium hover:bg-gray-700 transition"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            {err && <p className="mt-3 text-sm text-red-600">{err}</p>}

            <div className="mt-auto pt-4 flex justify-end">
              <img
                src={logo2}
                alt="Company logo 2"
                className="h-8 w-auto opacity-80 select-none pointer-events-none"
              />
            </div>
            </div>
          </div>

          {/* RIGHT: Three separate output panels */}
          <div className="bg-white rounded-2xl shadow-md flex flex-col h-full overflow-hidden lg:col-span-2">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold"><span className="font-mono bg-purple-100 text-green-700 px-2 py-1 rounded">Testing-AI</span> Generative Output</h2>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700 leading-none -translate-y-0.5">Streaming</span>
                <button
                  onClick={() => setIsStreamingEnabled(!isStreamingEnabled)}
                  disabled={loading || isPrintingRobot}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isStreamingEnabled ? 'bg-blue-600' : 'bg-gray-300'
                  } ${(loading || isPrintingRobot) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isStreamingEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex-1 p-5 flex flex-col gap-3 overflow-hidden min-h-0 relative">
              {/* Analysis Panel */}
              <div
                className={`bg-blue-50 rounded-xl flex-1 flex flex-col overflow-hidden min-h-0 ${
                  expandedPanel === 'analysis'
                    ? 'absolute top-5 left-5 right-5 bottom-5 z-10 shadow-2xl transition-all duration-700'
                    : expandedPanel && expandedPanel !== 'analysis'
                    ? 'opacity-30 transition-opacity duration-0'
                    : 'transition-all duration-0'
                }`}
              >
                <div className="relative">
                  <div className="px-4 py-2 bg-blue-100 rounded-t-xl">
                    <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                      📊 Analysis & Reasoning
                      <InfoIcon
                        theme="blue"
                        tooltip="Retruns a detailed view on the reasoning of the AI to answer the questions, based on provided data and query. "
                      />
                    </h3>
                  </div>
                  <ExpandButton panelId="analysis" isExpanded={expandedPanel === 'analysis'} onToggle={handlePanelClick} theme="blue" />
                  <CopyButton
                    content={analysis}
                    theme="blue"
                    className="absolute top-1/2 right-12 -translate-y-1/2"
                  />
                </div>
                <div className="flex-1 px-4 pb-4 overflow-y-auto min-h-0" ref={scrollRefAnalysis}>
                  <div className="whitespace-pre-wrap text-sm pt-2">{analysis}</div>

                  {/* Debug info */}
                  {loading && !analysis && (
                    <div className="text-gray-500 text-sm pt-2 italic">
                      Waiting for analysis content...
                    </div>
                  )}
                </div>
              </div>

              {/* Test Cases Panel */}
              <div
                className={`bg-green-50 rounded-xl flex-1 flex flex-col overflow-hidden min-h-0 ${
                  expandedPanel === 'testcases'
                    ? 'absolute top-5 left-5 right-5 bottom-5 z-10 shadow-2xl transition-all duration-700'
                    : expandedPanel && expandedPanel !== 'testcases'
                    ? 'opacity-30 transition-opacity duration-0'
                    : 'transition-all duration-0'
                }`}
              >
                <div className="relative">
                  <div className="px-4 py-2 bg-green-100 rounded-t-xl">
                    <h3 className="font-semibold text-green-800 flex items-center gap-2">
                      📝 Test Cases Description
                      <InfoIcon
                        theme="green"
                        tooltip="Displays the created test cases in text form."
                      />
                    </h3>
                  </div>
                  <ExpandButton panelId="testcases" isExpanded={expandedPanel === 'testcases'} onToggle={handlePanelClick} theme="green" />
                </div>
                <div className="flex-1 px-4 pb-4 overflow-y-auto min-h-0" ref={scrollRefTestCases}>
                  {/* Dynamic SUB-compartments within the green box */}
                  <CompartmentList
                    items={dynamicTestcases}
                    expandedState={expandedTestcases}
                    setExpandedState={setExpandedTestcases}
                    showOnButtonPress={false}
                    defaultExpanded={true}
                    theme="emerald"
                    icon="📋"
                    loadingMessage="Waiting for testcase content..."
                    loading={loading && dynamicTestcases.length === 0 && !testCasesText}
                  />

                  {/* No raw text display - only compartments */}

                </div>
              </div>

              {/* Robot Framework Code Panel */}
              <div
                className={`bg-gray-900 rounded-xl flex-1 flex flex-col overflow-hidden min-h-0 ${
                  expandedPanel === 'robot'
                    ? 'absolute top-5 left-5 right-5 bottom-5 z-10 shadow-2xl transition-all duration-700'
                    : expandedPanel && expandedPanel !== 'robot'
                    ? 'opacity-30 transition-opacity duration-0'
                    : 'transition-all duration-0'
                }`}
              >
                <div className="relative">
                  <div className="px-4 py-2 bg-gray-800 rounded-t-xl">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      🤖 Robot Framework Code
                      <InfoIcon
                        theme="gray"
                        tooltip="Displays executable code in the Robot Framework based on the created test cases"
                      />
                    </h3>
                  </div>
                  <ExpandButton panelId="robot" isExpanded={expandedPanel === 'robot'} onToggle={handlePanelClick} theme="terminal" />
                </div>
                <div className="flex-1 px-4 pb-4 overflow-y-auto min-h-0" ref={scrollRefCode}>
                  {/* Dynamic Robot Framework compartments - only show when print button was pressed */}
                  <CompartmentList
                    items={dynamicRobotTests}
                    expandedState={expandedRobotTests}
                    setExpandedState={setExpandedRobotTests}
                    showOnButtonPress={true}
                    buttonPressed={showRobotFramework}
                    defaultExpanded={false}
                    theme="terminal"
                    icon={<FaRegFileCode />}
                    loading={false}
                  />

                  {/* Fallback to raw code display if no compartments or showRobotFramework is false */}
                  {(!showRobotFramework || !Array.isArray(dynamicRobotTests) || dynamicRobotTests.length === 0) && (
                    <pre className="text-green-400 text-xs overflow-auto whitespace-pre-wrap bg-black bg-opacity-20 p-3 rounded">
                      {robotFrameworkCode ||
                       (savedRobotFrameworkCode && !loading && !showRobotFramework ? "Robot Framework code can be displayed." :
                        (isGeneratingRobot ? "Robot Framework code is generating!" :
                         "No Robot Framework code available."))}
                    </pre>
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}